import { Button, Progress } from "antd";
import { exportToExcel } from "../../utils/exportToExcel";
import { GlobalContext } from "../../components/layout/global-context/globalContext";
import { useContext, useState } from "react";
import AddNewNumbersFormModal from "./add-new-numbers-form-modal/AddNewNumbersFormModal";
import AddNewPoolFormModal from "./add-new-pool-form-modal/AddNewPoolFormModal";
import EditPoolFormModal from "./edit-pool-form-modal/EditPoolFormModal";
import TablePage from "../../components/table-page/TablePage";
import TextHighlighter from "../../utils/text-highlighter/TextHighlighter";
import {
  getPoolsData,
  ResponseGuiType,
  TransformedDataType,
} from "../../api/pool/getPoolsData";
import { rangeColors } from "./PoolProcessor";
import ManageNumbersFormModal from "./manage-numbers-form-modal/ManageNumbersFormModal";
import { AiOutlineFileExcel } from "react-icons/ai";

interface State {
  isModalVisible: boolean;
  isAddNewPoolModalVisible: boolean;
  isAddNewNumbersModalVisible: boolean;
  isManageNumbersModalVisible: boolean;
  isEditPoolModalVisible: boolean;
  editPoolDataRecord?: TransformedDataType;
  refreshDataToggle: boolean;
  submitError?: string;
  dataView: TransformedDataType[];
  dataViewAll: TransformedDataType[];

  dataSource: () => Promise<ResponseGuiType>;
}

function Pool() {
  const { user } = useContext(GlobalContext);
  const [state, setState] = useState<State>({
    isModalVisible: false,
    isAddNewNumbersModalVisible: false,
    isAddNewPoolModalVisible: false,
    isManageNumbersModalVisible: false,
    isEditPoolModalVisible: false,
    refreshDataToggle: true,
    dataView: [],
    dataViewAll: [],
    dataSource: getPoolsData,
  });

  const {
    isAddNewNumbersModalVisible,
    isAddNewPoolModalVisible,
    isManageNumbersModalVisible,
    isEditPoolModalVisible,
    editPoolDataRecord,
    refreshDataToggle,
    dataView,
    dataViewAll,
    dataSource,
  } = state;

  const buttons = [
    {
      title: "Add New Numbers",
      visible: user.isAdmin,
      onClick: () => {
        setState((prevState) => ({
          ...prevState,
          isAddNewNumbersModalVisible: true,
        }));
      },
    },
    {
      title: "Manage Numbers",
      visible: user.isAdmin,
      onClick: () => {
        setState((prevState) => ({
          ...prevState,
          isManageNumbersModalVisible: true,
        }));
      },
    },
    {
      title: "Add New Pool",
      visible: user.isAdmin,
      onClick: () => {
        setState((prevState) => ({
          ...prevState,
          isAddNewPoolModalVisible: true,
        }));
      },
    },
    {
      title: (
        <span className="flex">
          <AiOutlineFileExcel className="text-lg mr-1" />
          Export
        </span>
      ),
      visible: true,
      onClick: () => {
        exportToExcel(dataView, "Export-Pool", {
          // id: { displayAs: "ID", colWidth: 4 },
          name: { displayAs: "Pool Name", colWidth: 12 },
          availableCount: { displayAs: "Available", colWidth: 10 },
          assignedCount: { displayAs: "Assigned", colWidth: 10 },
          holdCount: { displayAs: "Hold", colWidth: 10 },
          reservedCount: { displayAs: "Reserved", colWidth: 10 },
          retiredCount: { displayAs: "Retired", colWidth: 10 },
          totalCount: { displayAs: "Total", colWidth: 10 },
          availablePercentage: { displayAs: "Available %", colWidth: 10 },
        });
      },
    },
  ];

  return (
    <div className="flex flex-col w-full">
      <div className="flex justify-between items-center mx-8 my-6">
        <h1 className="text-lg font-semibold text-bdo-text-01-charcoal">
          Pool
        </h1>
        <aside className="flex gap-3">
          {buttons.map((button, index) => {
            return (
              button.visible && (
                <Button
                  key={index}
                  onClick={button.onClick}
                  className="bdo-red-button"
                >
                  {button.title}
                </Button>
              )
            );
          })}
        </aside>
      </div>

      <div className="flex-row w-full">
        <TablePage
          refreshDataToggle={refreshDataToggle}
          setParentDataAll={(data) => {
            //complete record set
            setState((prevState) => ({
              ...prevState,
              dataViewAll: data,
            }));
          }}
          setParentDataView={(data) => {
            //filtered+sorted record set
            setState((prevState) => ({
              ...prevState,
              dataView: data,
            }));
          }}
          pageConfiguration={{
            pageName: "Pool",
            dataApi: dataSource,
            columnDefinitions: [
              {
                titleText: "Pool",
                dataIndex: "name",
                render: ({
                  dataRow,
                  cellText = "",
                  searchText = "",
                  ignoreChars = "",
                }) => {
                  return (
                    <Button
                      type="link"
                      onClick={() => {
                        console.log("onClick() cellText:%o", cellText);
                        setState((prevState) => ({
                          ...prevState,
                          editPoolDataRecord: dataRow as TransformedDataType,
                          isEditPoolModalVisible: true,
                        }));
                      }}
                      disabled={!user.isAdmin}
                      style={{
                        display: "inline-block",
                        whiteSpace: "normal",
                        wordBreak: "break-all",
                        textAlign: "left",
                      }}
                    >
                      <span style={{ userSelect: "text" }}>
                        <TextHighlighter
                          bodyText={cellText}
                          searchText={searchText}
                          ignoreChars={ignoreChars}
                        />
                      </span>
                    </Button>
                  );
                },
              },
              {
                titleText: "Available",
                dataIndex: "availableCount",
                sortAsNumber: true,
              },
              {
                titleText: "Assigned",
                dataIndex: "assignedCount",
                sortAsNumber: true,
              },
              {
                titleText: "Hold",
                dataIndex: "holdCount",
                sortAsNumber: true,
              },
              {
                titleText: "Reserved",
                dataIndex: "reservedCount",
                sortAsNumber: true,
              },
              {
                titleText: "Retired",
                dataIndex: "retiredCount",
                sortAsNumber: true,
              },
              {
                titleText: "Total",
                dataIndex: "totalCount",
                sortAsNumber: true,
              },
              {
                titleText: "Threshold",
                dataIndex: "threshold",
                sortAsNumber: true,
              },
              {
                titleText: "Available Percentage",
                dataIndex: "availablePercentageX",
                showFilter: false,
                render: ({ dataRow }) => {
                  const renderedPercentage =
                    Math.round(Number(dataRow.availablePercentage)) || 1;
                  return (
                    <Progress
                      percent={renderedPercentage}
                      strokeColor={
                        rangeColors[
                          Number(dataRow.availablePercentageX.split(":")[0])
                        ]
                      }
                      format={(percent) => (
                        <span
                          style={{ color: "black" }}
                        >{`${dataRow.availablePercentage}%`}</span>
                      )}
                    />
                  );
                },
              },
            ],
          }}
        />
        <AddNewNumbersFormModal
          isVisible={isAddNewNumbersModalVisible}
          handleClose={(refreshData) =>
            setState((prevState) => ({
              ...prevState,
              isAddNewNumbersModalVisible: false,
              ...(refreshData ? { refreshDataToggle: !refreshDataToggle } : {}),
            }))
          }
        />
        <ManageNumbersFormModal
          isVisible={isManageNumbersModalVisible}
          handleClose={(refreshData) =>
            setState((prevState) => ({
              ...prevState,
              isManageNumbersModalVisible: false,
              ...(refreshData ? { refreshDataToggle: !refreshDataToggle } : {}),
            }))
          }
        />
        <AddNewPoolFormModal
          isVisible={isAddNewPoolModalVisible}
          handleClose={(refreshData) =>
            setState((prevState) => ({
              ...prevState,
              isAddNewPoolModalVisible: false,
              ...(refreshData ? { refreshDataToggle: !refreshDataToggle } : {}),
            }))
          }
          allPools={dataViewAll}
        />
        <EditPoolFormModal
          isVisible={isEditPoolModalVisible}
          handleClose={(refreshData) =>
            setState((prevState) => ({
              ...prevState,
              isEditPoolModalVisible: false,
              ...(refreshData ? { refreshDataToggle: !refreshDataToggle } : {}),
            }))
          }
          editPoolRecordId={editPoolDataRecord?.id}
        />
      </div>
    </div>
  );
}

export default Pool;
